import { Divider, HorizontalScrollView, Stack } from '@/components/layout';
import { Image } from '@/components/ui';
import Text from '@/components/ui/text/text';
import { ContentWrapper } from '@/features/player-session/components/session-summary/content-wrapper';
import { usePinsLayoutCalc } from '@/features/player-session/components/session-summary/player-summary-card';
import { buildDetailsByLevel } from '@/features/player-session/utils';
import { CHECKPOINT_LEADER_DOTS } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { useScaling } from '@/hooks/use-scaling';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { Activity, memo, useMemo } from 'react';
import { StyleSheet, ViewStyle } from 'react-native';

const LevelSection = memo(
  ({
    section,
    sectionIdx,
  }: {
    section: ReturnType<typeof buildDetailsByLevel>['sections'][number];
    sectionIdx: number;
  }) => {
    const { scale, isTablet } = useScaling();
    const { contentWidth, pinGap, pinSize, overflowWidth, visibleWidth } = usePinsLayoutCalc(
      section.pins,
    );
    const scrollContentStyle = useMemo(() => ({ alignItems: 'center' as const }), []);
    const scrollViewStyle: ViewStyle = useMemo(
      () => ({
        width: visibleWidth,
        maxWidth: '100%',
        height: pinSize,
      }),
      [pinSize, visibleWidth],
    );
    return (
      <>
        <Stack key={section.level} gap={1} width="100%" paddingBottom={scale(4)}>
          <Activity mode={sectionIdx > 0 ? 'visible' : 'hidden'}>
            <Divider thickness={1} spacing={scale(12)} color={colors['3']} />
          </Activity>
          <Stack
            alignSelf="center"
            width={isTablet ? '100%' : overflowWidth - scale(12)}
            paddingX={isTablet ? scale(16) : undefined}
            marginY={scale(8)}>
            <Text
              variant="Font Montserrat/Subtitle/Minimize 2 Bold 10px"
              style={[detailStyles.levelHeader, sectionIdx > 0 && { marginTop: scale(12) }]}>
              LEVEL {section.level}
            </Text>
          </Stack>
          {section.pins.length > 0 && (
            <Stack
              paddingX={scale(16)}
              paddingTop={scale(8)}
              paddingBottom={scale(12)}
              align="center">
              <HorizontalScrollView
                horizontal
                bounces={false}
                showsHorizontalScrollIndicator={false}
                snapToInterval={pinSize + pinGap}
                contentContainerStyle={scrollContentStyle}
                style={scrollViewStyle}>
                <Stack direction="row" gapX={pinGap} height={pinSize} width={contentWidth}>
                  {section.pins.map(pin => (
                    <Image
                      key={`detail-${pin.imageUrl ?? 'pin'}-${pin.name}`}
                      source={{ uri: pin.imageUrl ?? '' }}
                      persistLoadedUri
                      style={[detailStyles.pinImage, { width: pinSize, height: pinSize }]}
                    />
                  ))}
                </Stack>
              </HorizontalScrollView>
            </Stack>
          )}
          {section.rows.map(row => (
            <Stack
              key={row.title}
              alignSelf="center"
              paddingX={isTablet ? scale(16) : undefined}
              width={isTablet ? '100%' : overflowWidth - scale(12)}>
              {row.statuses.map((status, statusIdx) => (
                <Stack
                  key={statusIdx}
                  direction="row"
                  align="center"
                  gap={scale(4)}
                  paddingTop={scale(4)}
                  width="100%">
                  {statusIdx === 0 ? (
                    <>
                      <Text variant="Font Inter/Subtitle/Regular" style={detailStyles.rowText}>
                        {row.title}
                      </Text>
                      <Text
                        numberOfLines={1}
                        ellipsizeMode="clip"
                        variant={'Font Inter/Subtitle/Bold 10px'}
                        style={detailStyles.dottedLine}>
                        {CHECKPOINT_LEADER_DOTS}
                      </Text>
                      <Text variant="Font Inter/Subtitle/Regular" style={detailStyles.rowText}>
                        {status}
                      </Text>
                    </>
                  ) : (
                    <Text variant="Font Inter/Subtitle/Regular" style={detailStyles.statusOnly}>
                      {status}
                    </Text>
                  )}
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      </>
    );
  },
);

export const PlayerDetailsSections = memo(
  ({
    detailsByLevel,
    level,
  }: {
    detailsByLevel: ReturnType<typeof buildDetailsByLevel>;
    level: number;
  }) => {
    const { scale } = useScaling();

    const restSections = useMemo(() => {
      return detailsByLevel.sections;
    }, [detailsByLevel.sections]);

    return (
      <ContentWrapper level={level}>
        {detailsByLevel.hasAttempts ? (
          <Stack direction="column" paddingY={scale(8)} width="100%">
            {restSections.map((section, sectionIdx) => (
              <LevelSection
                key={`${section.level}-${sectionIdx}`}
                section={section}
                sectionIdx={sectionIdx}
              />
            ))}
          </Stack>
        ) : (
          <Stack paddingX={scale(16)} paddingTop={scale(12)} paddingBottom={scale(24)}>
            <Text variant="Font Inter/Subtitle/Regular 12px" style={detailStyles.noAttemptsText}>
              {detailsByLevel.emptyMessage}
            </Text>
          </Stack>
        )}
      </ContentWrapper>
    );
  },
);

const detailStyles = StyleSheet.create({
  levelHeader: {
    color: theme.colors['10'],
  },
  rowText: {
    color: theme.colors['6'],
  },
  noAttemptsText: {
    color: colors['10'],
    textAlign: 'center',
    width: '100%',
  },
  statusOnly: {
    color: theme.colors['6'],
    textAlign: 'right',
    flex: 1,
  },
  dottedLine: {
    flex: 1,
    color: colors['6'],
  },
  pinImage: {
    overflow: 'hidden',
  },
});
